<template>
  <div>
    <CRow>
      <CCol sm="12">
        <CCard>
          <CCardHeader>
            <b>On/Off Planted Information</b>
          </CCardHeader>
          <CCardBody>
            <p class="text-muted">
              Halaman ini bersifat <b>informasi saja</b> (read-only), tidak ada tindakan yang dapat dilakukan di sini.
              Gunakan halaman ini untuk audit: status Planted saat ini, rekomendasi sistem, status absensi,
              riwayat notifikasi yang terkirim, dan riwayat perubahan status manual oleh ICL/admin.
            </p>

            <div class="form-group" style="max-width: 400px;">
              <label>Pilih Group iCare</label>
              <select class="form-control" v-model="selectedIcareId" @change="loadAudit">
                <option value="">-- Pilih Group iCare --</option>
                <option v-for="icare in icareList" :key="icare.id" :value="icare.id">
                  {{ icare.name }}
                </option>
              </select>
            </div>

            <div v-if="loading" class="text-muted">Memuat data...</div>

            <div v-if="!loading && selectedIcareId && rows.length === 0" class="text-muted">
              Tidak ada data untuk group iCare ini.
            </div>

            <div class="table-responsive" v-if="!loading && rows.length > 0">
              <table class="table table-bordered table-sm">
                <thead>
                  <tr>
                    <th>Nama</th>
                    <th>Role</th>
                    <th>Status Sekarang</th>
                    <th>Rekomendasi Sistem</th>
                    <th>Info Absen</th>
                    <th>Terakhir Hadir</th>
                    <th>Notifikasi Perlu Dikirim</th>
                    <th>Notifikasi Terkirim</th>
                    <th>Aksi Manual</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in rows" :key="row.uuid">
                    <td>{{ row.fullname }}</td>
                    <td class="text-capitalize">{{ row.role }}</td>
                    <td>
                      <CBadge :color="row.is_planted ? 'success' : 'secondary'">{{ row.current_status_label }}</CBadge>
                    </td>
                    <td>
                      <CBadge v-if="row.recommended_action === 'set_planted'" color="info">Set Planted</CBadge>
                      <CBadge v-else-if="row.recommended_action === 'unset_planted'" color="danger">Set Off-Planted</CBadge>
                      <CBadge v-else color="success">Sesuai</CBadge>
                      <div class="small text-muted mt-1">{{ row.recommended_reason }}</div>
                    </td>
                    <td>
                      <CBadge v-if="row.absence_notice === '3_month'" color="danger">3 Bulan</CBadge>
                      <CBadge v-else-if="row.absence_notice === '2_month'" color="danger">2 Bulan</CBadge>
                      <CBadge v-else-if="row.absence_notice === '1_month'" color="warning">1 Bulan</CBadge>
                      <span v-else>-</span>
                    </td>
                    <td>{{ row.last_attended_date || 'Belum pernah' }}</td>
                    <td>
                      <CBadge v-if="pendingNotifications(row).length > 0" color="warning">{{ pendingNotifications(row).length }} pending</CBadge>
                      <span v-else class="text-muted">Tidak ada</span>
                    </td>
                    <td>
                      <CBadge v-if="totalNotifications(row) > 0" color="info">{{ totalNotifications(row) }}x</CBadge>
                      <span v-else class="text-muted">Belum ada</span>
                    </td>
                    <td>
                      <CBadge v-if="row.manual_actions.length > 0" color="dark">{{ row.manual_actions.length }}x</CBadge>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td>
                      <CButton size="sm" color="secondary" variant="outline" @click="openDetail(row)">Lihat Detail</CButton>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <CModal title="Detail Audit" color="info" size="xl" :show.sync="detailModal">
      <div v-if="selected">
        <h5>{{ selected.fullname }} <small class="text-muted text-capitalize">({{ selected.role }})</small></h5>
        <p><b>Status Sekarang:</b> {{ selected.current_status_label }}</p>
        <p><b>Rekomendasi Sistem:</b> {{ selected.recommended_reason }}</p>
        <p v-if="selected.absence_reason"><b>Info Absen:</b> {{ selected.absence_reason }}</p>
        <p><b>Terakhir Hadir:</b> {{ selected.last_attended_date || 'Belum pernah tercatat hadir' }}</p>

        <hr>
        <h6>Status Notifikasi per Kasus</h6>
        <div class="table-responsive" v-if="selected.notifications_status.length > 0">
          <table class="table table-sm table-bordered">
            <thead>
              <tr>
                <th>Jenis Notifikasi</th>
                <th>Perlu Dikirim?</th>
                <th>Dikirim ke Siapa</th>
                <th>Sudah Terkirim (Periode Ini)?</th>
                <th>Terakhir Terkirim</th>
                <th>Status</th>
                <th>Trigger</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="n in selected.notifications_status" :key="n.notification_type">
                <td>
                  {{ n.label }}
                  <div class="small text-muted mt-1">{{ n.schedule_note }}</div>
                </td>
                <td>
                  <CBadge :color="n.should_send ? 'warning' : 'secondary'">{{ n.should_send ? 'Ya' : 'Tidak' }}</CBadge>
                </td>
                <td>
                  <div>{{ n.recipients_label }}</div>
                  <div class="small text-muted" v-if="n.recipients.length > 0">{{ n.recipients.join(', ') }}</div>
                  <div class="small text-muted" v-else-if="n.recipients_note">{{ n.recipients_note }}</div>
                </td>
                <td>
                  <CBadge :color="n.already_sent_this_cycle ? 'success' : 'secondary'">{{ n.already_sent_this_cycle ? 'Sudah' : 'Belum' }}</CBadge>
                </td>
                <td>{{ n.last_sent_at || '-' }}</td>
                <td>
                  <CBadge v-if="n.last_status === 'sent'" color="success">Sent</CBadge>
                  <CBadge v-else-if="n.last_status === 'failed'" color="danger">Failed</CBadge>
                  <CBadge v-else-if="n.last_status" color="secondary">{{ n.last_status }}</CBadge>
                  <span v-else class="text-muted">-</span>
                </td>
                <td>
                  <CBadge v-if="n.last_triggered_by === 'realtime'" color="info">Real-time</CBadge>
                  <CBadge v-else-if="n.last_triggered_by === 'cron'" color="dark">Cron</CBadge>
                  <span v-else class="text-muted">-</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="text-muted">Tidak ada kasus notifikasi yang berlaku untuk orang ini saat ini.</p>

        <hr>
        <h6>Riwayat Notifikasi Terkirim</h6>
        <div class="table-responsive" v-if="selected.notification_history.length > 0">
          <table class="table table-sm table-bordered">
            <thead>
              <tr>
                <th>Jenis Notifikasi</th>
                <th>Jumlah</th>
                <th>Terakhir Terkirim</th>
                <th>Status Terakhir</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="n in selected.notification_history" :key="n.notification_type">
                <td>{{ n.notification_type_label }}</td>
                <td>{{ n.count }}x</td>
                <td>{{ n.last_sent_at }}</td>
                <td>
                  <CBadge v-if="n.last_status === 'sent'" color="success">Sent</CBadge>
                  <CBadge v-else-if="n.last_status === 'failed'" color="danger">Failed</CBadge>
                  <CBadge v-else color="secondary">{{ n.last_status }}</CBadge>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="text-muted">Belum pernah ada notifikasi terkirim untuk orang ini.</p>

        <hr>
        <h6>Riwayat Perubahan Status Manual (oleh ICL/Admin)</h6>
        <div class="table-responsive" v-if="selected.manual_actions.length > 0">
          <table class="table table-sm table-bordered">
            <thead>
              <tr>
                <th>Waktu</th>
                <th>Perubahan</th>
                <th>Alasan</th>
                <th>Dilakukan Oleh</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(a, idx) in selected.manual_actions" :key="idx">
                <td>{{ a.created_at }}</td>
                <td>{{ a.old_value == '1' ? 'Planted' : 'Belum Planted' }} &rarr; {{ a.new_value == '1' ? 'Planted' : 'Belum Planted' }}</td>
                <td>{{ a.reason || '-' }}</td>
                <td>{{ a.actor_name || '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="text-muted">Belum ada perubahan status manual untuk orang ini.</p>
      </div>
      <template #footer>
        <CButton color="secondary" @click="detailModal = false">Tutup</CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import icare from '@/services/icare'
import icarePlantedAudit from '@/services/icarePlantedAudit'
import EventBus from '@/eventBus';

export default {
  name: 'PlantedAuditInfo',
  data () {
    return {
      icareList: [],
      selectedIcareId: '',
      rows: [],
      loading: false,
      detailModal: false,
      selected: null,
    }
  },
  mounted () {
    icare.getIcare().then(res => {
      this.icareList = res.data;
    }).catch((err) => {
      var params = {title: 'Error', mode:'danger', data: err.response && err.response.data ? err.response.data.message : err.message};
      EventBus.$emit('OPEN_MODAL_ALERT', params);
    })
  },
  methods: {
    loadAudit () {
      if (!this.selectedIcareId) {
        this.rows = [];
        return;
      }
      this.loading = true;
      icarePlantedAudit.getPlantedAudit(this.selectedIcareId).then(res => {
        this.rows = res.data;
        this.loading = false;
      }).catch((err) => {
        this.loading = false;
        var params = {title: 'Error', mode:'danger', data: err.response && err.response.data ? err.response.data.message : err.message};
        EventBus.$emit('OPEN_MODAL_ALERT', params);
      })
    },
    totalNotifications (row) {
      return row.notification_history.reduce((sum, n) => sum + n.count, 0);
    },
    pendingNotifications (row) {
      return (row.notifications_status || []).filter(n => n.should_send && !n.already_sent_this_cycle);
    },
    openDetail (row) {
      this.selected = row;
      this.detailModal = true;
    },
  }
}
</script>
